import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateCustomer, useCustomers, fullName } from "./hooks";
import { PageHeader } from "../../components/PageHeader";
import { CheckIcon, ChevronRightIcon, PlusIcon, SearchIcon, UserIcon, UsersIcon } from "../../components/icons";
import { formatARS } from "../../lib/money";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Sheet } from "../../components/Sheet";
import { Toast } from "../../components/Toast";

export function CustomerListView() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  useEffect(() => { const timer = setTimeout(() => setDebounced(query), 250); return () => clearTimeout(timer); }, [query]);
  const customers = useCustomers({ q: debounced.length >= 3 ? debounced : undefined, withBalance: true, limit: 200 });

  return (
    <main className="page page--narrow">
      <PageHeader title="Clientes" actions={<Button variant="primary" onClick={() => setCreateOpen(true)}><PlusIcon /> Nuevo cliente</Button>} />
      <section className="page-section">
        <div className="section-intro"><div><p className="eyebrow">Cuenta corriente</p><h1>Clientes</h1><p>Consultá deudas, saldos a favor y movimientos recientes.</p></div></div>
        <label className="search-control customer-search"><SearchIcon /><input type="search" aria-label="Buscar clientes" placeholder="Buscar por nombre, teléfono o email" value={query} onChange={(event) => setQuery(event.target.value)} /></label>
        {customers.isLoading && <div className="skeleton-list"><span /><span /><span /></div>}
        {customers.data?.length === 0 && <div className="empty-state"><UsersIcon width={28} height={28} /><strong>{query ? "Sin resultados" : "Sin clientes"}</strong><span>{query ? "Probá con otra búsqueda." : "Los clientes de cuenta corriente aparecerán acá."}</span></div>}
        <div className="customer-list">
          {customers.data?.map((customer) => {
            const balance = customer.balance?.balanceCents ?? 0;
            const tone = balance > 0 ? "debt" : balance < 0 ? "credit" : "ok";
            const label = balance > 0 ? "Debe" : balance < 0 ? "Saldo a favor" : "Al día";
            return (
              <button className={`customer-card customer-card--${tone}`} type="button" key={customer.id} onClick={() => navigate(`/customers/${customer.id}`)}>
                <span className="customer-card__avatar"><UserIcon /></span>
                <span className="customer-card__body"><strong>{fullName(customer)}</strong><small>{customer.phone ?? customer.email ?? "Sin datos de contacto"}</small></span>
                <span className="customer-card__balance"><em>{label}</em><strong className="num">{balance === 0 ? "$ 0" : formatARS(Math.abs(balance))}</strong></span>
                <ChevronRightIcon />
              </button>
            );
          })}
        </div>
      </section>
      <CreateCustomerSheet open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(id) => {
        setCreateOpen(false);
        setFeedback("Cliente creado");
        navigate(`/customers/${id}`);
      }} />
      {feedback && <Toast kind="success" message={feedback} icon={<CheckIcon />} autoCloseMs={2400} onDismiss={() => setFeedback(null)} />}
    </main>
  );
}

function CreateCustomerSheet({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (id: number) => void }) {
  const create = useCreateCustomer();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setFirstName("");
    setLastName("");
    setPhone("");
    setEmail("");
    setNote("");
    setError(null);
  }, [open]);

  return (
    <Sheet open={open} onClose={onClose} title="Nuevo cliente">
      <form className="form-stack" onSubmit={async (event) => {
        event.preventDefault();
        if (!firstName.trim()) return setError("El nombre es obligatorio.");
        if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setError("Ingresá un email válido.");
        try {
          const customer = await create.mutateAsync({
            firstName: firstName.trim(),
            lastName: lastName.trim() || null,
            phone: phone.trim() || null,
            email: email.trim() || null,
            note: note.trim() || null,
          });
          onCreated(customer.id);
        } catch {
          setError("No se pudo crear el cliente.");
        }
      }}>
        <div className="customer-form-grid">
          <Input label="Nombre" value={firstName} onChange={(event) => { setFirstName(event.target.value); setError(null); }} autoFocus />
          <Input label="Apellido (opcional)" value={lastName} onChange={(event) => setLastName(event.target.value)} />
        </div>
        <div className="customer-form-grid">
          <Input label="Teléfono (opcional)" value={phone} onChange={(event) => setPhone(event.target.value)} />
          <Input label="Email (opcional)" type="email" value={email} onChange={(event) => { setEmail(event.target.value); setError(null); }} />
        </div>
        <Input label="Nota (opcional)" value={note} onChange={(event) => setNote(event.target.value)} />
        {error && <p className="form-error">{error}</p>}
        <Button type="submit" fullWidth size="lg" disabled={create.isPending}>{create.isPending ? "Creando…" : "Crear cliente"}</Button>
      </form>
    </Sheet>
  );
}
